import { Button, IconButton } from '@mui/material';
import { Stack } from '@mui/material';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { usePrevious } from '@schooly/hooks/use-previous';
import {
  CheckIcon,
  CrossIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMain,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { CreateParentForm } from './CreateParentContent.type';
import { CustomFieldsFormContainer } from './CustomFieldsFormContainer';
import { RowsContainer } from './RowsContainer';

type UpdateExistingParentContentProps = {
  parent: CreateParentForm;
  onClose?: () => void;
  onSubmit: (parent: CreateParentForm) => void;
};

export const UpdateExistingParentContent: FC<UpdateExistingParentContentProps> = ({
  onClose,
  onSubmit,
  parent,
}) => {
  const { formatMessage } = useIntl();

  const form = useForm<CreateParentForm>({ defaultValues: parent, shouldFocusError: false });
  const prevParent = usePrevious(parent);

  useEffect(() => {
    if (!prevParent || prevParent.profile_id === parent.profile_id) return;

    form.reset(parent);
  }, [parent, prevParent, form.reset, form]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ModalHeader
          active
          title={
            <Stack gap={1} direction="row">
              <AvatarAuth user={parent} withAvatarPreview sx={{ width: 40, height: 40 }} />
              <TypographyWithOverflowHint variant="h1">
                {getUserFullName(parent)}
              </TypographyWithOverflowHint>
            </Stack>
          }
        >
          {onClose && (
            <IconButton onClick={onClose}>
              <CrossIcon />
            </IconButton>
          )}
        </ModalHeader>
        <ModalMain>
          <ModalContent active>
            <Stack pt={0.75}>
              <RowsContainer
                color="text.secondary"
                containerTitle="applications-AdditionalInformation"
                sx={{ gap: 0 }}
              >
                <CustomFieldsFormContainer />
              </RowsContainer>
            </Stack>
          </ModalContent>
        </ModalMain>

        <ModalFooter active>
          <Button type="submit" endIcon={<CheckIcon />}>
            {formatMessage({ id: 'action-Save' })}
          </Button>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};
